import React, { type ReactNode, useCallback, useEffect, type PropsWithChildren } from 'react';
import { EngagementSpotlight } from '@atlassiansox/engagekit-ts';
import {
	SpotlightTarget as AkSpotlightTarget,
	// @ts-expect-error - TS2305 - Module '"@atlaskit/onboarding"' has no exported member 'SpotlightTargetProps'.
	type SpotlightTargetProps,
} from '@atlaskit/onboarding';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { fg } from '@atlassian/jira-feature-gating';
import { QuickstartNudge } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/quickstart-nudge/main.tsx';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import { JWM_FEEDBACK_TASK } from '@atlassian/jira-onboarding-quickstart-core/src/common/constants.tsx';
import {
	useIsDismissed,
	useOpenTask,
	useQuickstartActions,
} from '@atlassian/jira-onboarding-quickstart-core/src/services/index.tsx';
import { FeedbackNudgeAsync } from '@atlassian/jira-software-onboarding-nudges--next/src/ui/jwm-feedback/async.tsx';
import { useHasOpened } from '@atlassian/jira-software-onboarding-quickstart-has-opened/src/services/has-opened/index.tsx';
import { HELP_SPOTLIGHT_KEY } from '../../common/constants.tsx';

export const SpotlightTargetComponent = componentWithCondition(
	() => getWillShowNav4() && fg('blu-5788-spotlight-help-button'),
	JWMFeedbackSpotlightWrapper,
	SpotlightTargetComponentOriginal,
);

function SpotlightTargetComponentOriginal({ children }: { children: ReactNode }) {
	return __SERVER__ ? (
		<>{children}</>
	) : (
		<>
			<SpotlightTarget name={HELP_SPOTLIGHT_KEY}>{children}</SpotlightTarget>
			{fg('add_engagement_spotlight_to_navbar_button') && (
				<EngagementSpotlight name={HELP_SPOTLIGHT_KEY} />
			)}
		</>
	);
}

function SpotlightTarget({ children, ...props }: SpotlightTargetProps) {
	return (
		<AkSpotlightTarget {...props}>
			{/*
			 * The atlaskit spotlight target component merely registers a ref on the child component,
			 *  so there is no way to give it a test id. We want to keep the test id as closely coupled
			 *  with the target as possible, so we add a span with id here, rather than on the child
			 *  where it could become more easily uncoupled.
			 */}
			<div data-test-id={`ak-spotlight-target-${props.name}`}>{children}</div>
		</AkSpotlightTarget>
	);
}

function JWMFeedbackSpotlightWrapper({ children }: PropsWithChildren) {
	const renderNudgeWrapper = useCallback(
		(kids: ReactNode) => <FeedbackNudgeAsync>{kids}</FeedbackNudgeAsync>,
		[],
	);
	const [{ hasOpened: shouldShowNudge }] = useHasOpened();
	const [isUnloaded] = useIsDismissed();
	const [openTask] = useOpenTask();
	const [, { setHasNudgeActive }] = useQuickstartActions();

	useEffect(() => {
		if (!isUnloaded && openTask === JWM_FEEDBACK_TASK && shouldShowNudge) {
			setHasNudgeActive(true);
		}
	}, [isUnloaded, openTask, shouldShowNudge, setHasNudgeActive]);

	return !__SERVER__ && shouldShowNudge ? (
		<QuickstartNudge renderWrapper={renderNudgeWrapper}>{children}</QuickstartNudge>
	) : (
		<SpotlightTargetComponentOriginal>{children}</SpotlightTargetComponentOriginal>
	);
}
